import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription, distinctUntilKeyChanged, filter, map } from 'rxjs';
import { AnalyticsService } from './analytics.service';
import { ModuleId } from './events.types';

interface NavEvent {
    path: string;
    location: string;
    title: string;
}

@Injectable({ providedIn: 'root' })
export class RouterTrackingService implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly doc = inject(DOCUMENT);
    private readonly analytics = inject(AnalyticsService);

    private sub?: Subscription;
    private lastLocation?: string;

    start(): void {
        if (this.sub) return;
        this.sub = this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                map((e) => this.toNavEvent(e)),
                distinctUntilKeyChanged('location')
            )
            .subscribe((nav) => {
                this.analytics.page({
                    page_path: nav.path,
                    page_title: nav.title,
                    page_location: nav.location,
                    page_referrer: this.lastLocation
                });
                this.lastLocation = nav.location;
            });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    private toNavEvent(e: NavigationEnd): NavEvent {
        const location = this.doc.defaultView?.location.href ?? e.urlAfterRedirects;
        const path = this.normalizePath(e.urlAfterRedirects);
        const title = this.resolveTitle(path);
        return { path, location, title };
    }

    /**
     * Normaliza path: usa routeConfig.path quando possível para evitar cardinalidade
     * alta no GA4 (ex.: /analise/123 → /analise/:id). Fallback para URL textual sem
     * query string.
     */
    private normalizePath(url: string): string {
        const segments = this.collectRouteConfigPath(this.route.snapshot.root);
        if (segments.length > 0) {
            return '/' + segments.join('/');
        }
        const noQuery = url.split('?')[0];
        return noQuery || '/';
    }

    private collectRouteConfigPath(snapshot: ActivatedRouteSnapshot | null): string[] {
        const out: string[] = [];
        let node: ActivatedRouteSnapshot | null = snapshot;
        while (node) {
            const p = node.routeConfig?.path;
            if (p) out.push(p);
            node = node.firstChild;
        }
        return out;
    }

    private resolveTitle(path: string): string {
        const module = this.moduleFromPath(path);
        const docTitle = this.doc.title || 'ScanUrb-GYN';
        return `${module} · ${docTitle}`;
    }

    private moduleFromPath(path: string): ModuleId {
        const first = path.split('/').filter(Boolean)[0];
        const known: ModuleId[] = [
            'inspecao',
            'apm',
            'mapeamento-urbano',
            'iptu',
            'analise',
            'feedbacks',
            'monitoramento',
            'auth'
        ];
        if (!first) return 'home';
        return (known.find((m) => m === first) ?? 'unknown');
    }
}
